import { addSentryBreadcrumb } from "../sentry.js";

/**
 * Login is a chain of awaited steps against the Discord embedded-app SDK and our
 * backend (see `initActivity` + `useAuth`). If any one of those promises never
 * settles — which is exactly what a browser ad/tracker blocker or strict privacy
 * setting can cause by silently dropping a background request — the app sits on
 * "Loading…" forever with nothing in the console, because nothing ever threw.
 *
 * `traceAuthStep` wraps each step so that:
 *  - every step leaves a breadcrumb + console.info marker (quiet at the default
 *    console level, but there when someone opens DevTools to trace a stuck login),
 *  - a step that hangs past `timeoutMs` is converted into a thrown
 *    `AuthTimeoutError` naming the exact step, so the silent stall becomes a
 *    visible, self-diagnosing error (and a Sentry report) instead of a spinner.
 */
export class AuthTimeoutError extends Error {
  constructor(
    public readonly step: string,
    public readonly timeoutMs: number,
  ) {
    super(`Login timed out at step "${step}" after ${Math.round(timeoutMs / 1000)}s`);
    this.name = "AuthTimeoutError";
  }
}

/**
 * User-facing hint shown when a login step times out. Written to read sensibly
 * whether or not the host app prefixes it (e.g. gamma's "Could not connect: …").
 * The 2026-07-31 reports all stalled at `sdk-ready` — the browser↔Discord
 * handshake — which a browser ad/tracker blocker, strict privacy setting, or
 * network can silently drop. The Discord desktop app avoids that layer entirely,
 * so it's listed first as the most reliable fix.
 */
export const AUTH_TIMEOUT_HINT =
  "The login step timed out — the app couldn't reach Discord to sign you in. " +
  "This is usually a browser ad/tracker blocker, a strict privacy setting, or a " +
  "network issue. Try the Discord desktop app (most reliable), turn off any ad " +
  "blocker for Discord, or open the app in a different browser such as Chrome. " +
  "Still stuck? Let us know in the 2nd Skies Discord and we'll help you get it sorted.";

const DEFAULT_STEP_TIMEOUT_MS = 15_000;

export async function traceAuthStep<T>(
  step: string,
  fn: () => Promise<T>,
  timeoutMs: number = DEFAULT_STEP_TIMEOUT_MS,
): Promise<T> {
  const start = performance.now();
  addSentryBreadcrumb(`auth: ${step} started`);
  console.info(`[auth] → ${step}`);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new AuthTimeoutError(step, timeoutMs)), timeoutMs);
  });

  try {
    const result = await Promise.race([fn(), timeout]);
    const ms = Math.round(performance.now() - start);
    addSentryBreadcrumb(`auth: ${step} ok`, { ms });
    console.info(`[auth] ✓ ${step} (${ms}ms)`);
    return result;
  } catch (err) {
    const ms = Math.round(performance.now() - start);
    addSentryBreadcrumb(`auth: ${step} failed`, { ms });
    console.error(`[auth] ✗ ${step} (${ms}ms)`, err);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Same as `traceAuthStep`, but retries the step when it *times out* — the
 * browser↔Discord handshake (`sdk-ready`) can fail transiently (a dropped
 * WebSocket, a momentary rate-limit), and a single retry with a fresh attempt
 * often succeeds. Only `AuthTimeoutError` is retried: a genuine rejection (e.g.
 * a bad client id) would just fail again, so it surfaces immediately.
 *
 * `fn` is invoked fresh on each attempt, so the caller can rebuild any stuck
 * state (e.g. construct a new DiscordSDK) rather than re-awaiting a dead one.
 */
export async function traceAuthStepWithRetry<T>(
  step: string,
  fn: () => Promise<T>,
  {
    attempts,
    timeoutMs,
    backoffMs = 500,
  }: { attempts: number; timeoutMs?: number; backoffMs?: number },
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    const label = attempt === 1 ? step : `${step} (retry ${attempt - 1})`;
    try {
      return await traceAuthStep(label, fn, timeoutMs);
    } catch (err) {
      lastErr = err;
      if (!(err instanceof AuthTimeoutError) || attempt === attempts) throw err;
      await delay(backoffMs);
    }
  }
  // Unreachable (the loop either returns or throws), but satisfies the compiler.
  throw lastErr;
}
